/**
 * AspectRatioSelector - Visual selector for image aspect ratios.
 *
 * Shows Google Ads compatible aspect ratios. The per-option description
 * used to render as inline helper text below the <select>; it now appears
 * in a small hover-delayed popover anchored to the info icon next to the
 * label — matches the Media Library `tooltip.css` pattern so the controls
 * strip stays compact.
 */
import { ASPECT_RATIO_OPTIONS } from '@/types/creativeStudio';
import type { AspectRatio } from '@/types/creativeStudio';
import { InformationCircleIcon } from '@heroicons/react/20/solid';
import '@/styles/tooltip.css';

interface AspectRatioSelectorProps {
  value: AspectRatio;
  onChange: (ratio: AspectRatio) => void;
  disabled?: boolean;
}

export function AspectRatioSelector({ value, onChange, disabled }: AspectRatioSelectorProps) {
  const selectedOption = ASPECT_RATIO_OPTIONS.find((opt) => opt.value === value);
  const description = selectedOption?.description;
  const tooltipId = description ? 'aspect-ratio-hint-tooltip' : undefined;

  return (
    <div>
      <div className="flex items-center gap-1 mb-2">
        <label htmlFor="aspect-ratio-select" className="block text-sm font-medium text-gray-700">
          Aspect Ratio
        </label>
        {description && (
          <div className="tooltip-container">
            <button
              type="button"
              aria-label={`About the ${selectedOption?.label} aspect ratio`}
              aria-describedby={tooltipId}
              className="flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 rounded"
            >
              <InformationCircleIcon className="h-4 w-4 text-gray-400" />
            </button>
            <span
              id={tooltipId}
              role="tooltip"
              className="tooltip-text tooltip-text-left"
              style={{ whiteSpace: 'normal', width: '18rem', maxWidth: '60vw', textAlign: 'left' }}
            >
              {description}
            </span>
          </div>
        )}
      </div>
      <select
        id="aspect-ratio-select"
        value={value}
        onChange={(e) => onChange(e.target.value as AspectRatio)}
        disabled={disabled}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm
                   focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                   disabled:bg-gray-100 disabled:text-gray-500"
      >
        {ASPECT_RATIO_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}
